import { useContext, useState, useEffect } from 'react';
import { AppContext } from '@/context/AppContext';
import { Sparkles, PlusCircle } from 'lucide-react';
import EmptyState from '@/components/shared/EmptyState';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { formatDomain } from '@/utils/formatUtils';
import * as api from '@/services/api';

export default function DiscoveryPanel() {
  const { activeClientId, activeClient, loadKeywords, addToast } = useContext(AppContext);
  const [discovered, setDiscovered] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const domain = activeClient?.domain;

  const fetchDiscovered = async () => {
    if (!activeClientId) return;
    setIsLoading(true);
    const { data, error } = await api.getDiscovered(activeClientId);
    if (error) {
      addToast(`Error fetching discovered keywords: ${error}`, 'error');
      setDiscovered([]);
    } else {
      setDiscovered(data || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchDiscovered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId]);

  const handleRunDiscovery = async () => {
    if (!activeClientId) return;
    setIsScanning(true);
    const { error } = await api.runDiscovery(activeClientId);
    if (error) {
      addToast(`Discovery scan failed: ${error}`, 'error');
    } else {
      addToast('Discovery scan complete! New keywords found.', 'success');
      await fetchDiscovered();
    }
    setIsScanning(false);
  };

  const handlePromote = async (kwId) => {
    const { error } = await api.promoteKeyword(activeClientId, kwId);
    if (error) {
      addToast(`Error promoting keyword: ${error}`, 'error');
    } else {
      addToast('Keyword added to tracking list!', 'success');
      setDiscovered(prev => prev.filter(k => k.id !== kwId));
      await loadKeywords(activeClientId);
    }
  };

  return (
    <div data-testid="discovery-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div className="section-label flex-1">
          <span>Discovery</span>
          <span className="hidden sm:block flex-1 h-px bg-[#1E2D45]" />
        </div>
        <button
          onClick={handleRunDiscovery}
          disabled={isScanning || !domain}
          className="button-primary text-xs sm:text-sm flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="run-discovery-btn"
        >
          {isScanning ? <LoadingSpinner size="sm" /> : <Sparkles size={16} />}
          Run Discovery Scan
        </button>
      </div>

      {!domain && (
        <EmptyState
          icon="🌐"
          title="No Domain Found"
          message="Please add a domain to the client profile before running discovery scans."
        />
      )}

      {domain && isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {domain && !isLoading && discovered.length === 0 && (
        <EmptyState
          icon="💡"
          title="No New Opportunities"
          message="Run a discovery scan to find new keyword opportunities for your client's domain."
        />
      )}

      {domain && !isLoading && discovered.length > 0 && (
        <div className="card p-4">
          <h4 className="text-sm font-bold font-syne text-[#E2E8F0] mb-4 text-center">
            Potential Keywords for <span className="text-[#00C48C]">{formatDomain(domain)}</span>
          </h4>
          
          {/* Mobile: Card Layout */}
          <div className="sm:hidden space-y-3">
            {discovered.map(kw => (
              <div 
                key={kw.id}
                className="flex items-center justify-between p-3 bg-[#0A0F1E] rounded-lg border border-[#1E2D45]"
                data-testid={`discovered-keyword-${kw.id}`}
              >
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-syne font-semibold text-[#E2E8F0] block break-words">
                    {kw.keyword}
                  </span>
                  <span className="text-xs text-[#94A3B8]">{kw.location}</span>
                </div>
                <button
                  onClick={() => handlePromote(kw.id)}
                  className="inline-flex items-center gap-1 text-sm font-syne font-semibold text-[#00C48C] hover:text-[#00FFCC] transition-colors flex-shrink-0 ml-3"
                  data-testid={`promote-keyword-mobile-${kw.id}`}
                >
                  <PlusCircle size={16} />
                  Track
                </button>
              </div>
            ))}
          </div>

          {/* Desktop: Table Layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase text-[#4A5568] border-b border-[#1E2D45]">
                  <th className="py-3 pr-4">Keyword</th>
                  <th className="py-3 pr-4">Location</th>
                  <th className="py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {discovered.map(kw => (
                  <tr 
                    key={kw.id} 
                    className="border-b border-[#1E2D45] last:border-b-0"
                    data-testid={`discovered-keyword-desktop-${kw.id}`}
                  >
                    <td className="py-3 pr-4">
                      <span className="text-sm font-syne font-semibold text-[#E2E8F0]">
                        {kw.keyword}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-sm text-[#94A3B8]">{kw.location}</span>
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => handlePromote(kw.id)}
                        className="inline-flex items-center gap-1 text-sm font-syne font-semibold text-[#00C48C] hover:text-[#00FFCC] transition-colors"
                        data-testid={`promote-keyword-${kw.id}`}
                      >
                        <PlusCircle size={14} />
                        Track
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
